"use client";

import { useEffect, useRef } from "react";

/**
 * Hook to play ambient sacred sounds (tanpura, bells) on the web.
 * Uses the standard Web Audio API (via HTML5 Audio element).
 *
 * @param audioUrl Optional first-party URL to the audio file.
 */
export const useSacredAudio = (audioUrl?: string) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioUrl) {
      audioRef.current = null;
      return;
    }

    const audio = new Audio(audioUrl);
    audio.loop = true;
    audioRef.current = audio;

    const playAudio = async () => {
      try {
        // Many browsers block auto-play until user interaction.
        // We catch this to avoid console noise.
        await audio.play();
      } catch (error) {
        console.warn("Sacred audio playback failed (possibly due to auto-play policy):", error);
      }
    };

    void playAudio();

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, [audioUrl]);

  return {
    stop: () => {
      if (!audioRef.current) return;
      const audio = audioRef.current;
      // Smooth fade out over 1.5 seconds
      const fadeInterval = setInterval(() => {
        if (audio.volume > 0.05) {
          audio.volume -= 0.05;
        } else {
          audio.pause();
          audio.volume = 1; // reset for next play
          clearInterval(fadeInterval);
        }
      }, 75);
    },
    play: () => {
      if (audioRef.current) {
        audioRef.current.volume = 1;
        void audioRef.current.play();
      }
    },
  };
};
